import { router } from "@inertiajs/react";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";
import ArrowIcon from "app/frontend/assets/icons/icn-carat.svg?react";
import { useCallback, useRef, useState } from "react";
import { PAGES } from "./constants";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </Box>
  );
}

export default function HeaderMenuMobile() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState<number>(0);

  const handleChange = (newValue: number) => {
    setValue(newValue);
    scrollRef.current?.scrollBy({
      left: scrollRef.current.clientWidth,
      behavior: "smooth",
    });
  };

  const handleNavigate = (route: string) => {
    router.visit(route);
  };

  const scrollBack = useCallback(() => {
    tabsRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [tabsRef]);

  return (
    <Box
      ref={scrollRef}
      sx={{
        width: "100vw", // Confines the view to one screen width
        overflowX: "hidden", // Hide the scrollbar itself
        display: "flex", // Crucial: sets up the horizontal layout
        flexWrap: "nowrap", // Prevents the children from wrapping below
        transition: "width 0.3s ease",
        height: "100%", // Take up full height of its container
      }}
    >
      <Box ref={tabsRef} sx={{ flexShrink: 0, width: "100vw" }}>
        <List>
          {PAGES.map((page, index) => (
            <ListItem
              key={`mobile-tab-${page.routes.main}-${index}`}
              sx={{ my: 3 }}
            >
              <ListItemButton
                onClick={(_e) => {
                  if (page.children.length) {
                    handleChange(index);
                  } else {
                    handleNavigate(page.routes.main);
                  }
                }}
                sx={{ width: "100%", textAlign: "left", p: 0 }}
              >
                {page.name({
                  hideArrow: !page.children.length,
                })}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <Box sx={{ flexShrink: 0, width: "100vw" }}>
        {PAGES.map((page, index) => (
          <TabPanel
            key={`mobile-tabpanel-${page.routes.main}-${index}`}
            value={value}
            index={index}
          >
            <Button
              variant="text"
              onClick={scrollBack}
              sx={{
                width: "100%",
                textAlign: "start",
                color: "common.black",
                padding: 0,
              }}
            >
              <ArrowIcon
                width={"2em"}
                style={{ transform: "rotate(90deg)", marginRight: 3 }}
              />
              &nbsp;
              {page.name({ hideArrow: true })}
            </Button>

            <List>
              {page.children.map((child, childIndex) => (
                <ListItem
                  key={`mobile-tabpanel-${page.routes.main}-child-${childIndex}`}
                  sx={{ my: 3, width: "100%" }}
                >
                  <ListItemButton
                    onClick={(_e) => {
                      handleNavigate(child.route);
                    }}
                    disabled={child.disabled}
                    sx={{ width: "100%", textAlign: "left", padding: 0 }}
                  >
                    {child.icon}
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 900,
                        width: "100%",
                      }}
                    >
                      {child.title}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
}
